import axios, { type AxiosError } from "axios";

/**
 * All calls go to api.memily.com/api/v1. Credentials on so the
 * session cookie set by /auth/apple flows across subsequent requests
 * to the same parent domain (.memily.com).
 */
export const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/api/v1`,
  withCredentials: true,
  timeout: 30_000,
});

/**
 * A memory-only bearer token — set by the /auth/apple response and
 * cleared on sign-out (and on any 401, see the interceptor below).
 * The cookie is authoritative; this is a belt-and-suspenders for
 * cross-XHR paths that some browsers gate more tightly. Kept out of
 * localStorage deliberately so a token can't outlive the tab.
 */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

/**
 * On any 401 the bearer token is stale — drop it so it can't ride on
 * later requests and diverge from the (also-expired) cookie. The
 * caller still gets the rejected promise to drive its own redirect.
 */
api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (httpStatus(error) === 401) {
      setAccessToken(null);
    }
    return Promise.reject(error);
  },
);

/**
 * The backend error envelope (Phase 3 item 17):
 * { error: { code, message, fields, request_id } }
 * Every error handler in the SPA reads through this shape.
 */
export interface ApiError {
  code: string;
  message: string;
  fields?: Record<string, string>;
  request_id?: string;
}

/** Axios-aware HTTP status extraction. Returns null for non-HTTP errors. */
export const httpStatus = (err: unknown): number | null => {
  if (!axios.isAxiosError(err)) return null;
  return err.response?.status ?? null;
};

export const extractApiError = (err: unknown): ApiError | null => {
  if (!axios.isAxiosError(err)) return null;
  const axErr = err as AxiosError<{ error?: ApiError }>;
  return axErr.response?.data?.error ?? null;
};
